import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";
import { take } from "rxjs";
import { IPostingPeriodDetails } from "./models/posting-period.model";
import { FinanceService } from "src/app/finance/finance.service";
import { LOCK_MODULES, TransactionNames } from "src/app/finance/constants/constants";

@Component({
    selector: "app-posting-period-selector",
    templateUrl: "./posting-period-selector.component.html",
})
export class PostingPeriodSelectorComponent implements  OnChanges {
    @Input({ required: true }) transactionDate!: Date;
    @Input({ required: true }) selectedPostingPeriod!: IPostingPeriodDetails;
    @Input({ required: true }) moduleName!: string;
    @Input({ required: true }) isViewMode: boolean = false;
    @Input() compactDropdown: boolean = false;
    @Output() postingInfoChange = new EventEmitter<IPostingPeriodDetails>();

    postingOptions: any[] = [];
    selectedPosting!: { label: string; value: any };

    readonly fullMonthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    readonly compactDropdownModules = ["Bill", "VendorCredit", "Item Receipt", "Invoice", "Credit Memo" , "ReceivePayment"];

    constructor(private financeService: FinanceService) {}


    ngOnChanges(changes: SimpleChanges): void {
      const hasModule = this.moduleName || changes['moduleName']?.currentValue;
      const hasPeriod = this.selectedPostingPeriod || changes['selectedPostingPeriod']?.currentValue;
      const prevDate = changes["transactionDate"]?.previousValue?.getTime?.() ?? 0;
      const currDate = changes["transactionDate"]?.currentValue?.getTime?.() ?? 0;
      
        if (changes["transactionDate"] && this.selectedPostingPeriod) {
            this.calculatePostingDate();
        }
        if (changes["selectedPostingPeriod"] && this.selectedPostingPeriod) {
            const { postingMonth, postingYear } = this.selectedPostingPeriod;
            if (postingMonth && postingYear) {
                this.setSelectedPosting(postingMonth, postingYear);
            }
        }

        if ((hasModule || hasPeriod) && (prevDate !== currDate)) {
        this.fetchPostingOptions();
      }
    }

    onPostingMonthChange(): void {
        this.calculatePostingDate();
    }

    private fetchPostingOptions(): void {
        this.financeService
            .getUnlockedPostingPeriods()
            .pipe(take(1))
            .subscribe((periods: any[]) => {
                const lockType = this.getLockType(this.moduleName);
                const filtered = lockType ? periods.filter((item) => item.closingYearLocks?.some((lock: any) => lock.lockName === lockType && lock.lockValue === false)) : periods;

                this.postingOptions = filtered.map((item) => ({
                    label: item.labelName,
                    value: item,
                    statusId: item.statusId,
                }));
                const { postingMonth, postingYear } = this.selectedPostingPeriod || {};
                const expectedLabel = `${this.fullMonthNames[postingMonth - 1]}/${postingYear}`;
                const isMonthPresent = this.postingOptions.some((opt) => opt.label === expectedLabel);
          
                if (!isMonthPresent && postingMonth && postingYear) {
                  this.postingOptions.push({
                    label: expectedLabel,
                    value: {
                      monthId: postingMonth,
                      year: postingYear,
                      labelName: expectedLabel,
                      closingYearLocks: [],
                      statusId: 1
                    },
                    isManual: true
                  });
                  this.postingOptions.sort((a, b) => {
                    const aYear = a.value.year;
                    const bYear = b.value.year;
                    const aMonth = a.value.monthId;
                    const bMonth = b.value.monthId;
                    return aYear !== bYear ? aYear - bYear : aMonth - bMonth;
                  });
                }
                if (!this.selectedPostingPeriod) {
                  const now = new Date();
                  const currentMonth = now.getMonth() + 1; 
                  const currentYear = now.getFullYear();
                  const currentLabel = `${this.fullMonthNames[currentMonth - 1]}/${currentYear}`;
          
                  const match = this.postingOptions.find(opt => opt.label === currentLabel);
                  if (match) {
                    this.selectedPosting = match;
                  } else {
                    const lastOption = this.postingOptions[this.postingOptions.length - 1];
                    this.selectedPosting = lastOption;
                  }
                  this.calculatePostingDate();
                } else {
                  this.setSelectedPosting(postingMonth, postingYear);
                }
            });
    }

    private getLockType(module: string): string | undefined {
        return Object.entries(LOCK_MODULES)
          .find(([_, modules]) => modules?.includes(module as TransactionNames))?.[0];
      }
    
      private setSelectedPosting(month: number, year: number): void {
        const label = `${this.fullMonthNames[month - 1]}/${year}`;
        const match = this.postingOptions.find(p => p.label === label);
        if (match) {
          this.selectedPosting = match;
          this.calculatePostingDate();
        }
      }

    private calculatePostingDate(): void {
        if (!this.transactionDate || !this.selectedPosting?.value) return;
        const postMonth = this.selectedPosting.value.monthId - 1;
        const postYear = this.selectedPosting.value.year;
        const txnDate = new Date(this.transactionDate);
        const txnDay = txnDate.getDate();
        const isPast = postYear * 12 + postMonth < txnDate.getFullYear() * 12 + txnDate.getMonth();
        const postingDate = isPast ? new Date(postYear, postMonth + 1, 0) : new Date(postYear, postMonth, txnDay);
        this.postingInfoChange.emit({
            postingDate,
            postingMonth: postMonth + 1,
            postingYear: postYear,
        });
    }
}
